import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { IoIosCheckmark } from 'react-icons/io';
import { IoChevronDownSharp } from 'react-icons/io5';
import { FeatureSwitch } from '../../../../offers/styles';
import {
  inputSelectCustomStyles,
  StyledSelect
} from '../../../../../components/CustomSearchBar';
import { ErrorText } from '../../../../../components/utils/AvatarCropper';
import { Palette } from '../../../../../components/utils';
import {
  CircleContainer,
  ImageConstraintsText,
  ImageDropzoneContainer,
  ImageUploadContainer,
  PreviewImage
} from '../../../../../components/ImageUploadWithPreviewV3';
import { imageString } from '../../../../../helper';
import { useGetConfigs } from '../../../../../hooks/useGetConfigs';
import {
  AMENITY_COST_TYPE,
  handleRequirePaymentUserChange
} from './amenityPaymentConfigV2';
import { MAX_ADVANCE_BOOKING_DAYS } from './addAmenityScheduleHelpersV2';
import { InputSeparator } from '../../../../settings/SettingsModals';
import { FieldBlock, FieldInnerFull, FieldLabel } from '../../../../../components/WizardV2/wizardStylesV2';
import {
  AmenityImagesAfterToggle,
  BookingLimitsFieldCol,
  BookingLimitsFieldsRow,
  BookingLimitsHeaderCopy,
  BookingLimitsHeaderDescription,
  BookingLimitsHeaderRow,
  BookingLimitsRowErrors,
  BookingLimitsSection,
  FieldRest,
  SelectInFieldWrapper,
  SlotDurationIntro,
  SlotDurationSection,
  ToggleBlock,
  ToggleCopy,
  ToggleDescription,
  ToggleRow,
  ToggleSwitchWrap,
  ToggleTitle,
  AmenityWizardNote,
  WizardTextInput,
  UnitPillButton,
  UnitPillDropdownContainer,
  UnitPillDropdownItem,
  UnitPillDropdownMenu
} from './addAmenityWizardStylesV2';

function UnitPillDropdown({ value, onChange, options }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => o.value === value) || options[0];

  return (
    <UnitPillDropdownContainer ref={containerRef}>
      <UnitPillButton $isOpen={isOpen} type="button" onClick={() => setIsOpen(!isOpen)}>
        {selectedOption?.label}
        <IoChevronDownSharp size={14} />
      </UnitPillButton>
      {isOpen && (
        <UnitPillDropdownMenu>
          {options.map((opt) => (
            <UnitPillDropdownItem
              key={opt.value}
              $selected={value === opt.value}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}>
              {opt.label}
              {value === opt.value && <IoIosCheckmark size={16} />}
            </UnitPillDropdownItem>
          ))}
        </UnitPillDropdownMenu>
      )}
    </UnitPillDropdownContainer>
  );
}

function AddAmenityRulesStepV2({
  t,
  control,
  errors,
  amenitiesLabels,
  building,
  allBuildings,
  waiverFile,
  setWaiverFile,
  clearErrors,
  setValue,
  trigger,
  initialAllowBooking
}) {
  const { orgConfig } = useGetConfigs();
  const td = (key, fallback) => t(key, { defaultValue: fallback });
  const buildingPhotoRaw =
    typeof building?.photo === 'string' && building.photo.trim() !== '' ? building.photo : '';
  const buildingLogoSrc = buildingPhotoRaw ? imageString(buildingPhotoRaw) : '';
  const allowBookingWatch = useWatch({ control, name: 'allow_booking' });
  const showBookingArchiveWarning =
    initialAllowBooking === true && allowBookingWatch === false;
  const requireReservationFormWatch = useWatch({
    control,
    name: 'require_reservation_form'
  });
  const allowMultiDayWatch = useWatch({ control, name: 'allow_multi_day' });
  const bookingLimitsUnitWatch = useWatch({ control, name: 'booking_limits_unit' });
  const minDurationValueWatch = useWatch({ control, name: 'min_duration_value' });
  const maxDurationValueWatch = useWatch({ control, name: 'max_duration_value' });
  const minReservationHoursWatch = useWatch({ control, name: 'min_reservation_hours' });
  const maxReservationHoursWatch = useWatch({ control, name: 'max_reservation_hours' });
  const selectedDaysWatch = useWatch({ control, name: 'selectedDays' });
  const dailyScheduleWatch = useWatch({ control, name: 'daily_schedule' });
  const slotDurationWatch = useWatch({ control, name: 'slot_duration_minutes' });
  const costTypeWatch = useWatch({ control, name: 'cost_type' });
  const limitsAreDays = Number(bookingLimitsUnitWatch) === 1;
  const prevBookingLimitsUnitRef = useRef(bookingLimitsUnitWatch);

  const canEnablePayments =
    !!building?.id &&
    !!orgConfig?.enable_vopay &&
    !!building?.config?.enable_vopay;

  // Min/max share schedule + each other; keep both errors in sync on any related change.
  useEffect(() => {
    if (!trigger || !allowBookingWatch) return;
    if (allowMultiDayWatch) {
      trigger(['min_duration_value', 'max_duration_value']);
      return;
    }
    trigger(['min_reservation_hours', 'max_reservation_hours']);
  }, [
    allowBookingWatch,
    allowMultiDayWatch,
    bookingLimitsUnitWatch,
    dailyScheduleWatch,
    maxDurationValueWatch,
    maxReservationHoursWatch,
    minDurationValueWatch,
    minReservationHoursWatch,
    selectedDaysWatch,
    slotDurationWatch,
    trigger
  ]);

  useEffect(() => {
    if (!setValue || allowMultiDayWatch) return;
    if (Number(costTypeWatch) === AMENITY_COST_TYPE.DAILY) {
      setValue('cost_type', AMENITY_COST_TYPE.NONE, {
        shouldDirty: true,
        shouldValidate: false
      });
    }
  }, [allowMultiDayWatch, costTypeWatch, setValue]);

  useEffect(() => {
    if (!requireReservationFormWatch) {
      setWaiverFile(null);
      clearErrors('waiver_upload');
    }
  }, [requireReservationFormWatch, setWaiverFile, clearErrors]);

  useEffect(() => {
    if (!setValue || !allowMultiDayWatch || Number(bookingLimitsUnitWatch) !== 1) return;
    const prev = prevBookingLimitsUnitRef.current;
    const unitJustSwitchedToDays = Number(prev) !== 1;
    prevBookingLimitsUnitRef.current = bookingLimitsUnitWatch;
    if (!unitJustSwitchedToDays) return;
    const min = minDurationValueWatch;
    if (min == null || min === '' || Number(min) === 0) {
      setValue('min_duration_value', 1, { shouldDirty: true, shouldValidate: true });
    }
  }, [allowMultiDayWatch, bookingLimitsUnitWatch, minDurationValueWatch, setValue]);

  const onWaiverDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles?.[0];
      if (file) {
        setWaiverFile(file);
        clearErrors('waiver_upload');
      }
    },
    [setWaiverFile, clearErrors]
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    accept: { 'application/pdf': [] },
    maxFiles: 1,
    maxSize: 5242880,
    disabled: !requireReservationFormWatch,
    onDrop: onWaiverDrop
  });

  const bookingLimitsUnitOptions = [
    { value: 1, label: t('add_amenity_duration_unit_days', { defaultValue: 'Days' }) },
    { value: 0, label: t('add_amenity_duration_unit_hours', { defaultValue: 'Hours' }) }
  ];

  return (
    <>
      <ToggleBlock>
        <ToggleRow>
          <ToggleCopy>
            <ToggleTitle>{t('Allow Bookings')}</ToggleTitle>
            <ToggleDescription>
              {td('add_amenity_allow_bookings_desc', 'Residents & PMs can book this amenity.')}
            </ToggleDescription>
          </ToggleCopy>
          <Controller
            control={control}
            name="allow_booking"
            render={({ field: { onChange, value } }) => (
              <ToggleSwitchWrap>
                <FeatureSwitch
                  shape="pill"
                  color="primary"
                  value={value}
                  checked={!!value}
                  onChange={onChange}
                />
              </ToggleSwitchWrap>
            )}
          />
        </ToggleRow>
        {showBookingArchiveWarning ? (
          <AmenityWizardNote>
            {t('Turning this off will archive all existing bookings.')}
          </AmenityWizardNote>
        ) : null}
      </ToggleBlock>

      {allowBookingWatch && (
        <>
          <ToggleRow>
            <ToggleCopy>
              <ToggleTitle>{t('Allow Multi-day Bookings')}</ToggleTitle>
              <ToggleDescription>
                {td('add_amenity_multi_day_desc', 'Amenity can be booked over multiple days.')}
              </ToggleDescription>
            </ToggleCopy>
            <Controller
              control={control}
              name="allow_multi_day"
              render={({ field: { onChange, value } }) => (
                <ToggleSwitchWrap>
                  <FeatureSwitch
                    shape="pill"
                    color="primary"
                    value={!!value}
                    checked={!!value}
                    onChange={onChange}
                  />
                </ToggleSwitchWrap>
              )}
            />
          </ToggleRow>

          <ToggleRow>
            <ToggleCopy>
              <ToggleTitle>{t('Allow Multiple Same-day Bookings')}</ToggleTitle>
              <ToggleDescription>
                {td(
                  'add_amenity_allow_same_day_multiple_desc',
                  'Residents can book this amenity more than once on the same day.'
                )}
              </ToggleDescription>
            </ToggleCopy>
            <Controller
              control={control}
              name="allow_same_day_multiple_bookings"
              render={({ field: { onChange, value } }) => (
                <ToggleSwitchWrap>
                  <FeatureSwitch
                    shape="pill"
                    color="primary"
                    value={value}
                    checked={!!value}
                    onChange={onChange}
                  />
                </ToggleSwitchWrap>
              )}
            />
          </ToggleRow>
        </>
      )}

      {allowBookingWatch && !allowMultiDayWatch && (
        <BookingLimitsSection>
          <ToggleTitle>{t('Booking Limits')}</ToggleTitle>
          <ToggleDescription>
            {td(
              'add_amenity_booking_limits_instruction',
              'You must enter min and max per reservation.'
            )}
          </ToggleDescription>
          <BookingLimitsFieldsRow>
            <BookingLimitsFieldCol>
              <FieldBlock>
                <FieldInnerFull>
                  <FieldLabel>{amenitiesLabels.min_hours}</FieldLabel>
                  <FieldRest>
                    <Controller
                      control={control}
                      name="min_reservation_hours"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <WizardTextInput
                          type="number"
                          id="wizard-min_reservation_hours"
                          min={0}
                          step={1}
                          placeholder="0"
                          autoComplete="off"
                          onBlur={onBlur}
                          onChange={(e) => {
                            const v = e.target.value;
                            onChange(v === '' ? 0 : Number(v));
                          }}
                          value={value != null && value !== '' ? String(value) : '0'}
                        />
                      )}
                    />
                  </FieldRest>
                </FieldInnerFull>
              </FieldBlock>
            </BookingLimitsFieldCol>
            <BookingLimitsFieldCol>
              <FieldBlock>
                <FieldInnerFull>
                  <FieldLabel>{amenitiesLabels.max_hours}</FieldLabel>
                  <FieldRest>
                    <Controller
                      control={control}
                      name="max_reservation_hours"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <WizardTextInput
                          type="number"
                          id="wizard-max_reservation_hours"
                          min={1}
                          step={1}
                          placeholder={t('Enter maximum time')}
                          autoComplete="off"
                          onBlur={onBlur}
                          onChange={(e) => {
                            const v = e.target.value;
                            onChange(v === '' ? null : Number(v));
                          }}
                          value={value != null && value !== '' ? String(value) : ''}
                        />
                      )}
                    />
                  </FieldRest>
                </FieldInnerFull>
              </FieldBlock>
            </BookingLimitsFieldCol>
          </BookingLimitsFieldsRow>
          {(errors?.min_reservation_hours || errors?.max_reservation_hours) && (
            <BookingLimitsRowErrors>
              {errors?.min_reservation_hours && (
                <ErrorText>{errors.min_reservation_hours.message}</ErrorText>
              )}
              {errors?.max_reservation_hours && (
                <ErrorText>{errors.max_reservation_hours.message}</ErrorText>
              )}
            </BookingLimitsRowErrors>
          )}
        </BookingLimitsSection>
      )}

      {allowBookingWatch && allowMultiDayWatch && (
        <BookingLimitsSection>
          <BookingLimitsHeaderRow>
            <BookingLimitsHeaderCopy>
              <ToggleTitle>{t('Booking Limits')}</ToggleTitle>
              <BookingLimitsHeaderDescription>
                {td(
                  'add_amenity_booking_limits_instruction',
                  'You must enter min and max per reservation.'
                )}
              </BookingLimitsHeaderDescription>
            </BookingLimitsHeaderCopy>
            <Controller
              control={control}
              name="booking_limits_unit"
              render={({ field: { onChange, value } }) => (
                <UnitPillDropdown
                  value={value != null && value !== '' ? value : 1}
                  onChange={onChange}
                  options={bookingLimitsUnitOptions}
                />
              )}
            />
          </BookingLimitsHeaderRow>
          {errors?.booking_limits_unit && (
            <ErrorText>{errors.booking_limits_unit.message}</ErrorText>
          )}
          <BookingLimitsFieldsRow>
            <BookingLimitsFieldCol>
              <FieldBlock>
                <FieldInnerFull>
                  <FieldLabel>
                    {limitsAreDays ? amenitiesLabels.min_days : amenitiesLabels.min_hours}
                  </FieldLabel>
                  <FieldRest>
                    <Controller
                      control={control}
                      name="min_duration_value"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <WizardTextInput
                          type="number"
                          id="wizard-min_duration_value"
                          min={0}
                          step={1}
                          placeholder={limitsAreDays ? '1' : td('add_amenity_min_duration_ph', '0')}
                          autoComplete="off"
                          onBlur={onBlur}
                          onChange={(e) => {
                            const v = e.target.value;
                            onChange(v === '' ? null : Number(v));
                          }}
                          value={value != null && value !== '' ? String(value) : ''}
                        />
                      )}
                    />
                  </FieldRest>
                </FieldInnerFull>
              </FieldBlock>
            </BookingLimitsFieldCol>
            <BookingLimitsFieldCol>
              <FieldBlock>
                <FieldInnerFull>
                  <FieldLabel>
                    {limitsAreDays ? amenitiesLabels.max_days : amenitiesLabels.max_hours}
                  </FieldLabel>
                  <FieldRest>
                    <Controller
                      control={control}
                      name="max_duration_value"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <WizardTextInput
                          type="number"
                          id="wizard-max_duration_value"
                          min={1}
                          step={1}
                          placeholder={td('add_amenity_max_duration_ph', 'Max')}
                          autoComplete="off"
                          onBlur={onBlur}
                          onChange={(e) => {
                            const v = e.target.value;
                            onChange(v === '' ? null : Number(v));
                          }}
                          value={value != null && value !== '' ? String(value) : ''}
                        />
                      )}
                    />
                  </FieldRest>
                </FieldInnerFull>
              </FieldBlock>
            </BookingLimitsFieldCol>
          </BookingLimitsFieldsRow>
          {(errors?.min_duration_value || errors?.max_duration_value) && (
            <BookingLimitsRowErrors>
              {errors?.min_duration_value && (
                <ErrorText>{errors.min_duration_value.message}</ErrorText>
              )}
              {errors?.max_duration_value && (
                <ErrorText>{errors.max_duration_value.message}</ErrorText>
              )}
            </BookingLimitsRowErrors>
          )}
        </BookingLimitsSection>
      )}

      {allowBookingWatch && (
        <SlotDurationSection>
          <SlotDurationIntro>
            <ToggleTitle>
              {td(
                'add_amenity_max_advance_section_title',
                'Limit How Far Residents Can Book Ahead'
              )}
            </ToggleTitle>
            <ToggleDescription>
              {td('add_amenity_max_advance_section_desc', 'Prevents advance bookings.')}
            </ToggleDescription>
          </SlotDurationIntro>
          <FieldBlock>
            <FieldInnerFull>
              <FieldLabel>
                {td('add_amenity_max_days_ahead', 'Max Days Ahead')}
              </FieldLabel>
              <FieldRest>
                <Controller
                  control={control}
                  name="max_advance_booking"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <WizardTextInput
                      type="number"
                      id="wizard-max_advance_booking"
                      min={1}
                      max={MAX_ADVANCE_BOOKING_DAYS}
                      step={1}
                      placeholder="30"
                      autoComplete="off"
                      onBlur={onBlur}
                      onChange={(e) => {
                        const v = e.target.value;
                        onChange(v === '' ? null : Number(v));
                      }}
                      value={value != null && value !== '' ? String(value) : ''}
                    />
                  )}
                />
              </FieldRest>
            </FieldInnerFull>
            {errors?.max_advance_booking && (
              <ErrorText>{errors.max_advance_booking.message}</ErrorText>
            )}
          </FieldBlock>
        </SlotDurationSection>
      )}

      {allowBookingWatch && (
        <ToggleRow>
          <ToggleCopy>
            <ToggleTitle>{t('Require Manager Approval')}</ToggleTitle>
            <ToggleDescription>
              {td('add_amenity_manager_approval_desc', 'Bookings are held until manager approval.')}
            </ToggleDescription>
          </ToggleCopy>
          <Controller
            control={control}
            name="require_manager_approval"
            render={({ field: { onChange, value } }) => (
              <ToggleSwitchWrap>
                <FeatureSwitch
                  shape="pill"
                  color="primary"
                  value={value}
                  checked={!!value}
                  onChange={onChange}
                />
              </ToggleSwitchWrap>
            )}
          />
        </ToggleRow>
      )}

      <InputSeparator>
        <ToggleCopy>
          <ToggleTitle>{t('Shared Amenity')}</ToggleTitle>
          <ToggleDescription>
            {t('add_amenity_shared_description', {
              defaultValue: 'This amenity can be shared between buildings.'
            })}
          </ToggleDescription>
        </ToggleCopy>
        <FieldBlock>
          <FieldInnerFull>
            <FieldLabel>{t('Select Buildings')}</FieldLabel>
            <Controller
              control={control}
              name="building_ids"
              render={({ field: { onChange, value } }) => (
                <StyledSelect
                  isMulti
                  options={
                    allBuildings
                      ? allBuildings
                          .filter((b) => b.id !== building?.id)
                          .map((b) => ({ value: b.id, label: b.name }))
                      : []
                  }
                  onChange={onChange}
                  value={value || []}
                  placeholder={t('Select buildings...')}
                  styles={inputSelectCustomStyles({ minHeight: '56px', height: 'auto' })}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              )}
            />
          </FieldInnerFull>
          {errors?.building_ids && (
            <ErrorText>{errors.building_ids.message}</ErrorText>
          )}
        </FieldBlock>
      </InputSeparator>

      {allowBookingWatch && (
        <>
          <ToggleRow>
            <ToggleCopy>
              <ToggleTitle>{t('Enable Payments')}</ToggleTitle>
              <ToggleDescription>
                {canEnablePayments
                  ? t('add_amenity_enable_payments_desc', {
                      defaultValue:
                        'Set booking rate, service fee, and deposit in the payment step.'
                    })
                  : t('add_amenity_enable_payments_unavailable_desc', {
                      defaultValue: 'Payments are not available for this building.'
                    })}
              </ToggleDescription>
            </ToggleCopy>
            <Controller
              control={control}
              name="require_payment"
              render={({ field: { onChange, value } }) => (
                <ToggleSwitchWrap>
                  <FeatureSwitch
                    shape="pill"
                    color="primary"
                    value={value}
                    checked={!!value}
                    disabled={!canEnablePayments || !allowBookingWatch}
                    onChange={(e) => {
                      const next = e.target.checked;
                      if (!next) {
                        handleRequirePaymentUserChange(false, onChange, {
                          setValue,
                          clearErrors
                        });
                        return;
                      }
                      if (!allowBookingWatch || !canEnablePayments) return;
                      handleRequirePaymentUserChange(true, onChange, {
                        setValue,
                        clearErrors
                      });
                    }}
                  />
                </ToggleSwitchWrap>
              )}
            />
          </ToggleRow>

          <ToggleRow>
            <ToggleCopy>
              <ToggleTitle>
                {t('add_amenity_require_amenity_form', { defaultValue: 'Require Amenity Form' })}
              </ToggleTitle>
              <ToggleDescription>
                {t('add_amenity_require_amenity_form_desc', {
                  defaultValue: 'Residents must complete and submit form.'
                })}
              </ToggleDescription>
            </ToggleCopy>
            <Controller
              control={control}
              name="require_reservation_form"
              render={({ field: { onChange, value } }) => (
                <ToggleSwitchWrap>
                  <FeatureSwitch
                    shape="pill"
                    color="primary"
                    value={value}
                    checked={!!value}
                    onChange={onChange}
                  />
                </ToggleSwitchWrap>
              )}
            />
          </ToggleRow>

          {requireReservationFormWatch ? (
            <AmenityImagesAfterToggle style={{ marginTop: 16 }}>
              <ImageDropzoneContainer style={{ marginTop: 0 }} {...getRootProps()}>
                <input {...getInputProps()} />
                <ImageUploadContainer gapY="24px" alignItems="center">
                  <CircleContainer $hasImage={!!buildingLogoSrc} aria-hidden>
                    {buildingLogoSrc ? (
                      <PreviewImage src={buildingLogoSrc} alt="" draggable={false} />
                    ) : null}
                  </CircleContainer>
                  <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        color: Palette.BLACK,
                        marginBottom: 13,
                        lineHeight: '24px'
                      }}>
                      {isDragActive ? (
                        t('add_amenity_waiver_drop_active', { defaultValue: 'Drop PDF here…' })
                      ) : (
                        <>
                          {t('add_amenity_waiver_dropzone_instruction_prefix', {
                            defaultValue: 'Drag and drop your PDF here, or '
                          })}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              open();
                            }}
                            style={{
                              margin: 0,
                              padding: 0,
                              border: 'none',
                              background: 'none',
                              color: Palette.BLACK,
                              fontSize: 14,
                              fontWeight: 600,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              fontFamily: 'inherit'
                            }}>
                            {t('add_amenity_waiver_browse_files', { defaultValue: 'browse files' })}
                          </button>
                          {t('add_amenity_waiver_dropzone_instruction_suffix', {
                            defaultValue: ' PDF only · 5MB max'
                          })}
                        </>
                      )}
                    </div>
                    {waiverFile ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          flexWrap: 'wrap'
                        }}>
                        <ImageConstraintsText
                          style={{
                            marginBottom: 0,
                            flex: '1 1 auto',
                            minWidth: 0,
                            wordBreak: 'break-word'
                          }}>
                          {waiverFile.name}
                        </ImageConstraintsText>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setWaiverFile(null);
                            clearErrors('waiver_upload');
                          }}
                          style={{
                            flexShrink: 0,
                            border: 'none',
                            background: 'transparent',
                            color: Palette.OCEANIC,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                            padding: '0 0 0 8px',
                            alignSelf: 'center'
                          }}>
                          {t('Remove')}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </ImageUploadContainer>
              </ImageDropzoneContainer>
              {errors?.waiver_upload?.message ? (
                <ErrorText>{errors.waiver_upload.message}</ErrorText>
              ) : null}
            </AmenityImagesAfterToggle>
          ) : null}
        </>
      )}
    </>
  );
}

export default AddAmenityRulesStepV2;
